import React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import MapIcon from "@mui/icons-material/Map";
import PlaceIcon from "@mui/icons-material/Place";
import EventIcon from "@mui/icons-material/Event";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import { getSafeTelHref, getSafeMailtoHref } from "./filterInstructors";
import {
    getActiveUpcomingCourses,
    COURSE_STATUS_LABELS,
    COURSE_STATUS_COLORS,
} from "./courseValidation";

const formatDateNorwegian = (dateStr) => {
    if (!dateStr) return "";
    try {
        const [year, month, day] = dateStr.split("-");
        if (!year || !month || !day) return dateStr;
        const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
        return date.toLocaleDateString("nb-NO", {
            day: "numeric",
            month: "short",
        });
    } catch {
        return dateStr;
    }
};

export const InstructorCard = ({
    instructor,
    onLocate,
    onExpressInterest,
    isSelected = false,
}) => {
    if (!instructor) return null;

    const hasCoordinates = typeof instructor.lat === "number" && typeof instructor.lon === "number";
    const telHref = getSafeTelHref(instructor.phone);
    const mailtoHref = getSafeMailtoHref(instructor.email);
    const activeCourses = getActiveUpcomingCourses(instructor);
    const nextCourse = activeCourses.length > 0 ? activeCourses[0] : null;

    return (
        <Card
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 3,
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                border: (theme) => `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                boxShadow: isSelected
                    ? "0 4px 16px rgba(40, 82, 56, 0.15)"
                    : "0 2px 8px rgba(0, 0, 0, 0.04)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.08)",
                },
            }}
        >
            <CardContent
                sx={{
                    p: {xs: 2, sm: 2.5},
                    pb: 1,
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    maxWidth: "100%",
                }}
            >
                {/* Header: Name and Profession */}
                <Box sx={{ mb: 1 }}>
                    <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                            fontWeight: 700,
                            color: "primary.main",
                            fontSize: "1.1rem",
                            lineHeight: 1.3,
                            mb: 0.5,
                            wordBreak: "break-word",
                        }}
                    >
                        {instructor.name}
                    </Typography>

                    {instructor.title ? (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                fontWeight: 500,
                                wordBreak: "break-word",
                                minHeight: 20,
                            }}
                        >
                            {instructor.title}
                        </Typography>
                    ) : (
                        <Box sx={{ height: 20 }} />
                    )}
                </Box>

                {/* Location Badge: Placed consistently below title */}
                <Box sx={{ mb: 1.5, minHeight: 28, display: "flex", alignItems: "center" }}>
                    {instructor.place ? (
                        <Chip
                            icon={<PlaceIcon sx={{ fontSize: "0.95rem !important" }} />}
                            label={instructor.place}
                            size="small"
                            variant="outlined"
                            sx={{
                                borderColor: "rgba(40, 82, 56, 0.2)",
                                bgcolor: "rgba(40, 82, 56, 0.04)",
                                color: "text.primary",
                                fontWeight: 600,
                                maxWidth: "100%",
                                "& .MuiChip-label": {
                                    maxWidth: {xs: 220, sm: 260},
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                },
                            }}
                        />
                    ) : (
                        <Box sx={{ height: 24 }} />
                    )}
                </Box>

                {/* Upcoming Course Banner */}
                {nextCourse && (
                    <Box
                        sx={{
                            mb: 1.5,
                            p: 1.25,
                            borderRadius: 2,
                            bgcolor: "rgba(40, 82, 56, 0.06)",
                            border: "1px solid rgba(40, 82, 56, 0.16)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            flexWrap: "wrap",
                        }}
                    >
                        <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                            <EventIcon sx={{fontSize: 16, color: "primary.main"}} />
                            <Typography variant="caption" sx={{fontWeight: 700, color: "primary.main"}}>
                                Oppstart {formatDateNorwegian(nextCourse.startDate)}
                            </Typography>
                        </Box>
                        <Chip
                            label={COURSE_STATUS_LABELS[nextCourse.status] || nextCourse.status}
                            color={COURSE_STATUS_COLORS[nextCourse.status] || "default"}
                            size="small"
                            sx={{
                                height: 22,
                                fontSize: "0.725rem",
                                fontWeight: 700,
                                ...(nextCourse.status === "few_seats" && {
                                    bgcolor: "#FEF3C7",
                                    color: "#92400E",
                                    border: "1px solid #FCD34D",
                                }),
                            }}
                        />
                    </Box>
                )}

                {/* Description with Uniform 3-Line Clamp */}
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        lineHeight: 1.6,
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: 68,
                        flexGrow: 1,
                        mb: 1,
                    }}
                >
                    {instructor.description || "Ingen nærmere beskrivelse oppgitt."}
                </Typography>
            </CardContent>

            {/* Action Buttons: Pinned to Bottom */}
            <CardActions
                sx={{
                    px: {xs: 1.5, sm: 2},
                    pb: 2,
                    pt: 0,
                    mt: "auto",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                    width: "100%",
                }}
            >
                {typeof onExpressInterest === "function" && (
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<ContactMailIcon sx={{fontSize: 18}} />}
                        onClick={() => onExpressInterest(instructor, nextCourse)}
                        aria-label={`Meld interesse for kurs hos ${instructor.name}`}
                        sx={{
                            flex: "1 1 100%",
                            minHeight: 44,
                            px: 1.5,
                            fontSize: "0.85rem",
                            textTransform: "none",
                            borderRadius: 2,
                            fontWeight: 700,
                            bgcolor: "#285238",
                            color: "#FFFFFF",
                            boxShadow: "none",
                            "&:hover": {
                                bgcolor: "#1f402c",
                            },
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        Meld interesse for kurs
                    </Button>
                )}

                {telHref && (
                    <Button
                        component="a"
                        href={telHref}
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<PhoneIcon sx={{fontSize: 18}} />}
                        aria-label={`Ring ${instructor.name} på ${instructor.phone}`}
                        sx={{
                            flex: {xs: "1 1 calc(50% - 8px)", sm: 1},
                            minHeight: 44,
                            minWidth: 0,
                            px: 1,
                            fontSize: "0.825rem",
                            textTransform: "none",
                            borderRadius: 2,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        Ring
                    </Button>
                )}

                {mailtoHref && (
                    <Button
                        component="a"
                        href={mailtoHref}
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<EmailIcon sx={{fontSize: 18}} />}
                        aria-label={`Send e-post til ${instructor.name}`}
                        sx={{
                            flex: {xs: "1 1 calc(50% - 8px)", sm: 1},
                            minHeight: 44,
                            minWidth: 0,
                            px: 1,
                            fontSize: "0.825rem",
                            textTransform: "none",
                            borderRadius: 2,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        E-post
                    </Button>
                )}

                {hasCoordinates && typeof onLocate === "function" && (
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<MapIcon sx={{fontSize: 18}} />}
                        onClick={() => onLocate(instructor)}
                        aria-label={`Vis ${instructor.name} i kart`}
                        sx={{
                            flex: {xs: "1 1 100%", sm: 1},
                            minHeight: 44,
                            minWidth: 0,
                            px: 1,
                            fontSize: "0.825rem",
                            textTransform: "none",
                            borderRadius: 2,
                            fontWeight: 600,
                            boxShadow: "none",
                            whiteSpace: "nowrap",
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        I kart
                    </Button>
                )}
            </CardActions>
        </Card>
    );
};
